import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Textarea } from '@/components/elements/textarea'
import { Modal } from '@/components/Modal'
import { addCertificate } from '@/services/certificates.service'
import { toast } from '@/utils/helpers'

const CreateCertificateModal = ({ entityId, organizationId, close }) => {
  const form = useForm({ defaultValues: { name: '', description: '' } })
  const resetForm = (id = null) => {
    form.reset({ name: '', description: '' })
    close(id)
  }

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateCertificate } = useMutation({
    mutationFn: (data) => addCertificate({ entityId, organizationId, ...data }),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} created successfully`)
      resetForm(data.id)

      // Refresh certificates list
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
    },
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => mutateCertificate(data)

  return (
    <Modal
      actions={[
        {
          label: 'Cancel',
          onClick: () => close(),
          type: 'cancel',
        },
        {
          label: 'Create Course',
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input id="name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea id="description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      }
      loading={isPending}
      title="Create New Course"
      setOpen={resetForm}
    />
  )
}

CreateCertificateModal.propTypes = {
  entityId: PropTypes.string,
  organizationId: PropTypes.string,
  close: PropTypes.func.isRequired,
}

export default CreateCertificateModal
